// Which effect a resolved rune expression actually invokes.
//
// `casts.ts` holds the effects themselves. This holds the one decision in
// front of them: given a concept, an operator, some modifiers, what the caster is
// facing and what they have marked, *which* effect fires and with what
// parameters. Until now that decision lived in `game/src/ui.ts` as an if/else
// chain interleaved with the narration for each branch, which was fine while the
// game was the only thing that could cast. It is not fine with a second caster
// (the editor), because the chain is where several real rules live -- that
// Transfer·Momentum is a physical shove rather than a properties mutation, that
// Project ignores the faced target entirely, that transferring a thing into
// itself is a no-op, that Separate only acts on Mass -- and a second copy of
// those would be a second set of rules to keep in sync.
//
// Deliberately split into *deciding* and *applying*. The game has to put its
// renderer between the two (a cast's SimEvents become tweens on the way past, see
// GameScene's applySimEvents), so it takes the decision and drives its own
// handler; a headless caller takes both. Neither reimplements the rule.
//
// This is not "hardcoding a rune combination to an effect" (see
// design/06_Architecture.md) -- nothing here knows about *specific* combinations
// like fire+ice. It is the operator's own meaning: Transfer moves a quantity
// between two loci, Project throws one outward, Separate splits mass.
import { ConceptId } from '../runes/concepts';
import { OperatorId } from '../runes/operators';
import { magnitudeScale } from '../runes/resolve';
import { WorldState } from './state';
import { SimEvents } from './events';
import {
  BindEffectEvent,
  BindEffectResult,
  ChargeBindEffectEvent,
  ChargeBindEffectResult,
  EnergyBindEffectEvent,
  EnergyBindEffectResult,
  EntropyBindEffectEvent,
  EntropyBindEffectResult,
  MomentumEffectEvent,
  MomentumEffectResult,
  ProjectEffectEvent,
  ProjectEffectResult,
  SeparateEffectEvent,
  SeparateEffectResult,
  TransferEffectEvent,
  TransferEffectResult,
  UnbindEffectEvent,
  UnbindEffectResult,
  UnbindChargeEffectEvent,
  UnbindChargeEffectResult,
  UnbindEnergyEffectEvent,
  UnbindEnergyEffectResult,
  UnbindEntropyEffectEvent,
  UnbindEntropyEffectResult,
  applyBindEffect,
  applyChargeBindEffect,
  applyEnergyBindEffect,
  applyEntropyBindEffect,
  applyMomentumEffect,
  applyProjectEffect,
  applySeparateEffect,
  applyTransferEffect,
  applyUnbindEffect,
  applyUnbindChargeEffect,
  applyUnbindEnergyEffect,
  applyUnbindEntropyEffect,
} from './casts';

/** Everything the decision below depends on. `targetObjectId` is whatever the
 * caster is facing ('self' when that is nothing), `sourceObjectId` whatever they
 * have marked ('self' when nothing) -- both are ids rather than objects because
 * the marked source deliberately survives leaving the room (see state.ts's
 * MarkedSource) and may not resolve to anything here. */
export interface CastRequest {
  conceptId: ConceptId;
  operatorId: OperatorId;
  modifierIds: string[];
  targetObjectId: string;
  sourceObjectId: string;
}

/** Which effect to invoke, and the event to invoke it with. `none` covers every
 * expression that resolves to real cost and narration but changes nothing in the
 * world -- Measure, Convert, a Bind/Separate on anything but Momentum (or Mass,
 * for Separate) with a real target, and a Transfer from a locus into itself.
 * Those are not failures; most of the rune language is not yet wired to a world
 * effect, and saying so with a case is better than saying it by falling off the
 * end of a chain. */
export type CastAction =
  | { kind: 'none' }
  | { kind: 'transfer'; event: TransferEffectEvent }
  | { kind: 'momentum'; event: MomentumEffectEvent }
  | { kind: 'project'; event: ProjectEffectEvent }
  | { kind: 'separate'; event: SeparateEffectEvent }
  | { kind: 'bind'; event: BindEffectEvent }
  | { kind: 'unbind'; event: UnbindEffectEvent }
  | { kind: 'energy-bind'; event: EnergyBindEffectEvent }
  | { kind: 'unbind-energy'; event: UnbindEnergyEffectEvent }
  | { kind: 'charge-bind'; event: ChargeBindEffectEvent }
  | { kind: 'unbind-charge'; event: UnbindChargeEffectEvent }
  | { kind: 'entropy-bind'; event: EntropyBindEffectEvent }
  | { kind: 'unbind-entropy'; event: UnbindEntropyEffectEvent };

/** What the effect reported back, tagged so a caller can narrate it. Mirrors
 * CastAction case for case. */
export type CastOutcome =
  | { kind: 'none' }
  | { kind: 'transfer'; result: TransferEffectResult }
  | { kind: 'momentum'; result: MomentumEffectResult }
  | { kind: 'project'; result: ProjectEffectResult }
  | { kind: 'separate'; result: SeparateEffectResult }
  | { kind: 'bind'; result: BindEffectResult }
  | { kind: 'unbind'; result: UnbindEffectResult }
  | { kind: 'energy-bind'; result: EnergyBindEffectResult }
  | { kind: 'unbind-energy'; result: UnbindEnergyEffectResult }
  | { kind: 'charge-bind'; result: ChargeBindEffectResult }
  | { kind: 'unbind-charge'; result: UnbindChargeEffectResult }
  | { kind: 'entropy-bind'; result: EntropyBindEffectResult }
  | { kind: 'unbind-entropy'; result: UnbindEntropyEffectResult };

/** The decision. Pure: it reads no world state and mutates nothing, which is what
 * lets the game take the answer and route it through its renderer while a test or
 * the editor applies it directly. */
export function castAction(request: CastRequest): CastAction {
  const { conceptId, operatorId, modifierIds, targetObjectId, sourceObjectId } = request;
  const { scale } = magnitudeScale(modifierIds);
  const reversed = modifierIds.includes('reverse');
  const radial = modifierIds.includes('radial');

  // Momentum's Transfer is a physical push, not a properties-bag mutation, and is
  // always self-sourced -- there is no "borrow someone else's momentum" locus --
  // so it bypasses the marked source entirely.
  if (operatorId === 'transfer' && conceptId === 'momentum' && targetObjectId !== 'self') {
    return { kind: 'momentum', event: { targetObjectId, scale } };
  }
  if (operatorId === 'transfer') {
    // Both loci being the same thing is a no-op rather than an error: the target
    // defaults to the caster when nothing is faced, and the source defaults to the
    // caster when nothing is marked, so self-into-self is what an unequipped cast
    // at open floor naturally produces.
    if (sourceObjectId === targetObjectId) return { kind: 'none' };
    return { kind: 'transfer', event: { conceptId, sourceObjectId, targetObjectId, reversed, scale } };
  }
  // Project has no target object at all -- it acts on whatever is within range of
  // the caster's own position and facing, which is why it is the one operator
  // that fires identically whether or not anything is faced.
  if (operatorId === 'project') {
    return { kind: 'project', event: { conceptId, radial, scale, reversed } };
  }
  if (operatorId === 'separate' && conceptId === 'mass' && targetObjectId !== 'self') {
    return { kind: 'separate', event: { targetObjectId, reversed } };
  }
  // Bind·Momentum links the faced object's movement to the marked Source --
  // the same source resolution Transfer already uses, defaulting to the
  // caster when nothing is marked. Binding a locus to itself is the same
  // no-op Transfer's self-into-self case already is.
  if (operatorId === 'bind' && conceptId === 'momentum' && targetObjectId !== 'self') {
    if (sourceObjectId === targetObjectId) return { kind: 'none' };
    return { kind: 'bind', event: { targetObjectId, sourceObjectId, mirrored: reversed } };
  }
  // Separate·Momentum releases an existing Bind·Momentum link -- Bind creates
  // the coupling, Separate undoes it, the same create/undo shape the operator
  // pair already has for Mass (fuse) vs. Mass·Reverse (remerge).
  if (operatorId === 'separate' && conceptId === 'momentum' && targetObjectId !== 'self') {
    return { kind: 'unbind', event: { targetObjectId } };
  }
  // Bind·Energy opens a thermal-contact link between the faced object and the
  // marked Source -- the same source resolution every other Bind reading
  // already uses, right down to the self-into-self no-op guard. Reverse is
  // captured the same way Bind·Momentum captures it (`mirrored: reversed`)
  // purely so the link can say what was cast -- it has no equilibration-side
  // effect yet, see EnergyBindDef's own doc comment.
  if (operatorId === 'bind' && conceptId === 'energy' && targetObjectId !== 'self') {
    if (sourceObjectId === targetObjectId) return { kind: 'none' };
    return { kind: 'energy-bind', event: { targetObjectId, sourceObjectId, mirrored: reversed } };
  }
  // Separate·Energy releases an existing Bind·Energy link -- Bind creates the
  // link, Separate undoes it, the same create/undo shape as Momentum and Mass.
  if (operatorId === 'separate' && conceptId === 'energy' && targetObjectId !== 'self') {
    return { kind: 'unbind-energy', event: { targetObjectId } };
  }
  // Bind·Charge strings a wire between the faced object and the marked Source
  // -- again the same source resolution and the same self-into-self no-op
  // guard. See ChargeBindDef for the mechanic and for why Reverse is captured
  // (`mirrored: reversed`) but does nothing yet, exactly as Bind·Energy's is.
  if (operatorId === 'bind' && conceptId === 'charge' && targetObjectId !== 'self') {
    if (sourceObjectId === targetObjectId) return { kind: 'none' };
    return { kind: 'charge-bind', event: { targetObjectId, sourceObjectId, mirrored: reversed } };
  }
  // Separate·Charge cuts an existing Bind·Charge wire. This is a deliberate
  // reassignment: `resolve()` used to read Separate·Charge as electrification
  // (splitting neutral charge into + and - regions), which is unbuildable
  // while `properties` floors at 0 and no negative Charge exists anywhere in
  // the simulation. Making the operator pair mean create/undo here keeps one
  // rule for the player to learn across all four Bind readings instead of
  // three plus an exception -- see design/07_Open_Questions.md.
  if (operatorId === 'separate' && conceptId === 'charge' && targetObjectId !== 'self') {
    return { kind: 'unbind-charge', event: { targetObjectId } };
  }
  // Bind·Entropy crosses the faced object and the marked Source over, so that a
  // later cast naming either one is served by the other -- a displacement, which
  // conserves by construction because the link only changes *which* two loci a
  // Transfer moves between. Same source resolution and same self-into-self no-op
  // guard as every other Bind. Reverse is captured (`mirrored: reversed`) purely
  // for the cast-rune indicator. See EntropyBindDef.
  if (operatorId === 'bind' && conceptId === 'entropy' && targetObjectId !== 'self') {
    if (sourceObjectId === targetObjectId) return { kind: 'none' };
    return { kind: 'entropy-bind', event: { targetObjectId, sourceObjectId, mirrored: reversed } };
  }
  // Separate·Entropy severs an existing Bind·Entropy coupling -- the same
  // create/undo shape the operator pair now has for all four Bind readings.
  if (operatorId === 'separate' && conceptId === 'entropy' && targetObjectId !== 'self') {
    return { kind: 'unbind-entropy', event: { targetObjectId } };
  }
  return { kind: 'none' };
}

/** Applies a decided action to a world. For callers with no renderer to thread
 * events through -- a test, the editor. The game deliberately does not use this:
 * it takes the action and calls the same handler through its own bridge, so the
 * SimEvents pass its renderer on the way. */
export function applyCastAction(world: WorldState, action: CastAction, out: SimEvents): CastOutcome {
  switch (action.kind) {
    case 'transfer':
      return { kind: 'transfer', result: applyTransferEffect(world, action.event, out) };
    case 'momentum':
      return { kind: 'momentum', result: applyMomentumEffect(world, action.event, out) };
    case 'project':
      return { kind: 'project', result: applyProjectEffect(world, action.event, out) };
    case 'separate':
      return { kind: 'separate', result: applySeparateEffect(world, action.event, out) };
    case 'bind':
      return { kind: 'bind', result: applyBindEffect(world, action.event) };
    case 'unbind':
      return { kind: 'unbind', result: applyUnbindEffect(world, action.event) };
    case 'energy-bind':
      return { kind: 'energy-bind', result: applyEnergyBindEffect(world, action.event, out) };
    case 'unbind-energy':
      return { kind: 'unbind-energy', result: applyUnbindEnergyEffect(world, action.event) };
    case 'charge-bind':
      return { kind: 'charge-bind', result: applyChargeBindEffect(world, action.event, out) };
    case 'unbind-charge':
      return { kind: 'unbind-charge', result: applyUnbindChargeEffect(world, action.event) };
    case 'entropy-bind':
      return { kind: 'entropy-bind', result: applyEntropyBindEffect(world, action.event, out) };
    case 'unbind-entropy':
      return { kind: 'unbind-entropy', result: applyUnbindEntropyEffect(world, action.event) };
    case 'none':
      return { kind: 'none' };
  }
}

/** Decide and apply in one step. */
export function cast(world: WorldState, request: CastRequest, out: SimEvents): CastOutcome {
  return applyCastAction(world, castAction(request), out);
}
